import { nhost } from "nhost";

export type ImageTransform = {
	quality?: number;
	width?: number;
	height?: number;
	image?: boolean;
};

/**
 * A usable URL for a stored file, for `<img>`, `<iframe>` and friends.
 *
 * Presigned rather than public. The storage service authenticates by
 * `Authorization` header only, and an element `src` cannot send one — that was
 * fine while every file was world-readable, but files are now readable only
 * through the node that references them, so an unauthenticated request is
 * refused. The presign call carries the session; the URL it returns carries its
 * own signature and expires. `DownloadButton` already worked this way, which is
 * why downloads kept working when inline display stopped.
 *
 * Image transforms are appended afterwards: they still apply on top of a
 * presigned URL (the signature does not cover them), so thumbnails stay
 * thumbnails instead of shipping multi-megabyte originals.
 */
const fileUrl = async (
	fileId: string,
	{ quality, width = 500, height, image }: ImageTransform = {},
): Promise<string | undefined> => {
	const { presignedUrl } = await nhost.storage.getPresignedUrl({ fileId });
	const url = presignedUrl?.url;
	if (!url) return undefined;
	if (!image) return url;

	const params = new URLSearchParams();
	if (width) params.set("w", String(width));
	if (height) params.set("h", String(height));
	if (quality) params.set("q", String(quality));
	const query = params.toString();
	if (!query) return url;

	return `${url}${url.includes("?") ? "&" : "?"}${query}`;
};

export default fileUrl;
